import axios from 'axios';
import {getOperatingSystem} from '../utils/miscellaneous';
import {
  BASE_URL,
  CLIENT_ASSESS_URL,
  CLIENT_DEMOGRAPHICS_URL,
  CLIENT_PRODUCT_RECOMMEND_URL,
  GET_ALL_GAMEZOP_GAME,
  CLIENT_DOKTOR_STRIP_URL,
  CLIENT_DOKTOR_SHOW_RESULTS,
  CLIENT_SOCIAL_SHARING_POST_DOKTOR,
  CLIENT_SOCIAL_SHARING_GET_DOKTOR,
  CLIENT_ASSESS_GET_URL,
  CLIENT_LAB_TEST_RECOMMEND_URL,
} from './constants';

import {API_KEYS} from './clientconfig';

/**
 * Hook for interacting with the client API endpoints.
 * @param {string} clientType - Type of client from CLIENT_TYPES
 * @returns {Object} An object containing functions to interact with the client API
 */
export const useClientApi = (clientType, sessionId) => {
  const api = axios.create({
    headers: {
      Authorization: API_KEYS[clientType],
      'Content-Type': 'application/json',
    },
  });

  //header for client stripe one time payment API
  const clientStripeApi = axios.create({
    headers: {
      Authorization: sessionId,
      'Content-Type': 'application/json',
    },
  });

  // api for social sharing content type changed
  const clientSocilaSharingApi = axios.create({
    headers: {
      Authorization: API_KEYS[clientType],
      'Content-Type': 'multipart/form-data',
    },
  });

  /**
   * Posts demographics data for assessment.
   *
   * @param {Object} demographics - User demographic data
   * @returns {Promise} Promise resolving to the assessment response with redirect URL and session ID
   */
  const postDemographics = async demographics => {
    const data = {
      device_type: getOperatingSystem().toUpperCase(),
      demographics: {
        age: demographics.age,
        gender: demographics.gender,
        height: demographics.height,
        weight: demographics.weight,
      },
    };

    return api.post(CLIENT_ASSESS_URL, data);
  };

  /**
   * Gets demographics data for assessment.
   * @param {*} sessionId
   * @returns {Promise} Promise resolving to the assessment response with redirect URL and session ID and demographics data
   */

  const getAssessmentBySession = async sessionId => {
    return api.get(`${CLIENT_ASSESS_GET_URL}/${sessionId}?device_type=${getOperatingSystem().toUpperCase()}`);
  };

  /**
   * Posts symptoms data for product recommendations.
   *
   * @param {string} sessionId - Session ID from assessment
   * @param {string} symptomsDescription - User symptoms description
   * @returns {Promise} Promise resolving to success status
   */
  const postSymptoms = async (sessionId, symptomsDescription, resultsToken) => {
    const data = symptomsDescription
      ? {
          symptoms_description: symptomsDescription,
          symptoms_started_at: new Date().toISOString(),
        }
      : {};

    const tokenParam = resultsToken && resultsToken !== 'null' ? resultsToken : '';

    try {
      const response = await api.post(`${CLIENT_PRODUCT_RECOMMEND_URL}/${sessionId}`, data, {
        params: {
          result: tokenParam,
        },
      });

      // Return the entire response data which may include conditions if symptoms were provided
      return response.data;
    } catch (error) {
      console.error('Error posting symptoms:', error);
      throw error;
    }
  };

  // Gets gamezop games data.
  const getGameZopGames = async sessionId => {
    return api.get(`${GET_ALL_GAMEZOP_GAME}`, {
      params: {
        session_id: sessionId,
      },
    });
  };

  /**
   * Gets product recommendations based on assessment.
   *
   * @param {string} sessionId - Session ID from assessment
   * @returns {Promise} Promise resolving to product recommendations
   */
  const getProductRecommendations = async sessionId => {
    return api.get(`${CLIENT_PRODUCT_RECOMMEND_URL}/${sessionId}`);
  };

  /**
   * Gets assessment results.
   *
   * @param {string} resultsToken - Encoded results token
   * @returns {Promise} Promise resolving to assessment results/vitals
   */
  // const getResults = async (resultsToken) => {
  //   return api.get(`${BASE_URL}/client/show-result`, {
  //     params: {
  //       results: resultsToken
  //     }
  //   });
  // };
  const getResults = async sessionId => {
    return api.get(`${BASE_URL}/client/show-result`, {
      params: {
        session_id: sessionId,
      },
    });
  };

  /**
   * Posts other extra demographics data  .
   *
   * @param {Object} demographics - User extra demographic data
   * @returns {Promise} Promise resolving to the assessment response with status code
   */
  const postDemographicsNew = async (extraDemographics, assessmentId) => {
    console.log('demographics', extraDemographics);

    const data = {
      date_of_birth: extraDemographics.birthDate,
      gender_id: extraDemographics.gender,
      ethnicity_id: extraDemographics.ethinicity,
      height: extraDemographics.height,
      weight: extraDemographics.weight,
      is_frontend_height_imperial_units: extraDemographics.isFrontendHeightImperialUnits,
      is_frontend_weight_imperial_units: extraDemographics.isFrontendWeightImperialUnits,
      is_smoker: extraDemographics.isSmoke,
      diabetes_type_id: extraDemographics.isDiabetes,
      has_high_bp: extraDemographics.hasHighBP,
      has_had_stroke: extraDemographics.hasHadStroke,
      has_had_heart_attack: extraDemographics.hasHadAttack,
      is_on_bp_meds: extraDemographics.isOnBpMeds,
      pregnant: extraDemographics.isPregnant,
      menopausal_status: extraDemographics.menopausalStatus,
      has_medical_condition: extraDemographics.hasMedicalCondition,
      medical_condition_name: extraDemographics.medicalConditionNames,
      has_allergies: extraDemographics.hasAllergies,
      allergy_name: extraDemographics.allergyNames,
      has_supplement: extraDemographics.hasSupplements,
      supplement_name: extraDemographics.supplements,
      wellness_goals: extraDemographics.wellnessGoals,
    };
    // "assessment_id": 250
    // return api.post(`${CLIENT_DEMOGRAPHICS_URL}/${assessmentId}`, data);
    return api.post(`${CLIENT_DEMOGRAPHICS_URL}?assessment_id=${encodeURIComponent(assessmentId)}`, data);
  };

  const postOneTimeCheckoutForDoktor = async () => {
    return clientStripeApi.post(`${CLIENT_DOKTOR_STRIP_URL}`);
  };

  const showResultsAfterSingleSessionPayment = async sessionId => {
    return api.get(`${CLIENT_DOKTOR_SHOW_RESULTS}`, {
      params: {
        session_id: sessionId,
      },
    });
  };

  const postClientSocialShareImg = async (sessionId, formData) => {
    return clientSocilaSharingApi.post(`${CLIENT_SOCIAL_SHARING_POST_DOKTOR}`, formData, {
      params: {
        session_id: sessionId, // Send session_id in the query parameters
      },
    });
  };

  const getClientSocialShareImg = async (sessionId, imgUrl) => {
    return clientSocilaSharingApi.get(`${CLIENT_SOCIAL_SHARING_GET_DOKTOR}${imgUrl}`, {
      params: {
        session_id: sessionId,
      },
    });
  };

  /**
   * Fetches lab test recommendations for a given assessment ID.
   *
   * @param {string|number} assessmentId - The assessment ID to fetch recommendations for
   * @returns {Promise} Promise resolving to lab test recommendations
   */
  const getLabTestRecommendations = async assessmentId => {
    try {
      const response = await api.get(`${CLIENT_LAB_TEST_RECOMMEND_URL}/${assessmentId}`);
      return response;
    } catch (error) {
      console.error('Error fetching lab test recommendations:', error);
      throw error;
    }
  };

  return {
    postDemographics,
    postSymptoms,
    getProductRecommendations,
    getResults,
    postDemographicsNew,
    getGameZopGames,
    postOneTimeCheckoutForDoktor,
    showResultsAfterSingleSessionPayment,
    postClientSocialShareImg,
    getClientSocialShareImg,
    getAssessmentBySession,
    getLabTestRecommendations,
  };
};
